/**
 * Character filters shared by InputField keydown + paste handlers.
 * Keep paste and keydown on the same allowed set.
 */

export type InputSanitizeMode = {
  onlyAlphabets?: boolean;
  onlyNumeric?: boolean;
  allowDecimal?: boolean;
  allowSpecialChars?: boolean;
  type?: string;
};

export const SPECIAL_CHAR_RE = /[!@#$%^&*()_+\=\[\]{};':"\\|,.<>\/?`~]/;
export const SINGLE_ALPHABET_RE = /^[a-zA-Z]$/;
export const SINGLE_DIGIT_RE = /^[0-9]$/;

const NON_TEXT_LIKE_INPUT_TYPES = new Set([
  "date",
  "time",
  "datetime-local",
  "month",
  "week",
  "color",
  "range",
  "file",
  "hidden",
  "checkbox",
  "radio",
  "button",
  "submit",
  "reset",
  "image",
]);

/** True when InputField applies a character filter that paste must honor. */
export function hasInputCharFilter({
  onlyAlphabets = false,
  onlyNumeric = false,
  allowSpecialChars = false,
  type = "text",
}: InputSanitizeMode): boolean {
  if (onlyAlphabets) return true;
  // Numeric mode is rendered as type="text" in InputField — selection-safe.
  if (type === "number" || onlyNumeric) return true;
  if (type === "email") return false;
  // date/time widgets: selectionStart throws in Chromium; leave native paste alone.
  if (NON_TEXT_LIKE_INPUT_TYPES.has(type)) return false;
  if (!allowSpecialChars) return true;
  return false;
}

/**
 * Sanitize clipboard/pasted text only (never re-filter a whole prefilling field value).
 */
export function sanitizeInputValue(
  rawValue: string,
  {
    onlyAlphabets = false,
    onlyNumeric = false,
    allowDecimal = false,
    allowSpecialChars = false,
    type = "text",
  }: InputSanitizeMode,
): string {
  if (onlyAlphabets) {
    return rawValue.replace(/[^a-zA-Z ]/g, "");
  }

  const isNumericMode = type === "number" || onlyNumeric;
  if (isNumericMode) {
    if (allowDecimal) {
      let seenDot = false;
      let out = "";
      for (const ch of rawValue) {
        if (/[0-9]/.test(ch)) {
          out += ch;
        } else if (ch === "." && !seenDot) {
          out += ch;
          seenDot = true;
        }
      }
      return out;
    }
    return rawValue.replace(/[^0-9]/g, "");
  }

  if (type !== "email" && !allowSpecialChars) {
    return rawValue.replace(new RegExp(SPECIAL_CHAR_RE.source, "g"), "");
  }

  return rawValue;
}
